import { Badge, Tooltip, TooltipTrigger } from "@light-technology/components";
import { useEffect } from "react";
import { Navigate, useLocation, useParams } from "react-router";
import { playbook } from "../../state/playbook";
import { useTypedDispatch } from "../../state/store";
import { enumLookup } from "../../utilities/enum";
import { playbookGuideLink, playbookSectionLink } from "../../utilities/links";
import { useDocumentTitle } from "../../utilities/useDocumentTitle";
import { PlaybookBody } from "./PlaybookBody";
import { PlaybookBreadcrumb } from "./PlaybookBreadcrumb";
import { PlaybookContentLayout } from "./PlaybookContentLayout";
import { PlaybookDisclaimer } from "./PlaybookDisclaimer";
import { PlaybookGuideCardRow } from "./PlaybookGuideCardRow";
import { PlaybookGuideFAQs } from "./PlaybookGuideFAQs";
import { INTEGRATIONS, PLAN_TYPES } from "./constants";
import { usePlaybookGuide, useSectionGuides } from "./hooks";
import { GuideBodySkeleton, SkeletonLine } from "./skeletons";

const formatDate = (dateString: string) => {
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
  };
  return new Date(dateString).toLocaleDateString(undefined, options);
};

const PlaybookBadge = ({
  label,
  badgeLabel,
  icon: Icon,
  tooltip,
}: {
  label: string;
  badgeLabel?: string;
  icon?: React.FC<React.SVGProps<SVGSVGElement>>;
  tooltip?: string;
}) => (
  <Tooltip title={tooltip} isDisabled={!tooltip} arrow>
    <TooltipTrigger>
      <Badge size="md">
        {Icon && <Icon className="mr-1 size-3 text-utility-gray-500" />}
        {badgeLabel ?? label}
      </Badge>
    </TooltipTrigger>
  </Tooltip>
);

export const PlaybookGuidePage = () => {
  const { shortId } = useParams<{ shortId: string }>();
  const { guide, section, isLoading } = usePlaybookGuide(shortId);
  const { guides: sectionGuides } = useSectionGuides(section?.slug);
  const { pathname } = useLocation();
  const dispatch = useTypedDispatch();

  // Mark the guide as viewed when it loads
  useEffect(() => {
    if (guide) {
      dispatch(playbook.actions.markGuideViewed(guide));
    }
  }, [dispatch, guide]);

  useDocumentTitle(guide?.title, "Playbook");

  const breadcrumbItems = [
    { label: "Playbook", to: "/playbook" },
    section
      ? {
          label: section.title,
          to: playbookSectionLink(section),
        }
      : null,
    guide ? { label: guide.title } : null,
  ];

  // TODO: 404 page?
  if (!guide && !isLoading) return <Navigate to="/playbook" replace />;

  // Fix URL if it doesn't match current data
  if (guide && pathname !== playbookGuideLink(guide)) {
    return <Navigate to={playbookGuideLink(guide)} replace />;
  }

  const integrationConfig = guide
    ? enumLookup(INTEGRATIONS, guide.integration)
    : null;

  const otherSectionGuides = sectionGuides.filter(
    (sg) => sg.short_id !== guide?.short_id,
  );

  return (
    <div className="mx-auto w-full max-w-4xl pt-16 pb-8xl">
      <PlaybookBreadcrumb className="mb-md" items={breadcrumbItems} />
      <article className="flex flex-col gap-7xl">
        <div className="flex flex-col gap-md">
          <h1 className="max-w-xl text-display-md font-semibold tracking-tight text-primary md:text-display-lg">
            {guide ? guide.title : <SkeletonLine className="w-2/3" />}
          </h1>
          <p className="max-w-lg text-md text-tertiary">
            {guide ? guide.tagline : <SkeletonLine className="w-full" subtle />}
          </p>
          <div className="flex flex-wrap gap-md">
            {guide ? (
              <>
                {guide.plan_types.map((pt) => {
                  const config = enumLookup(PLAN_TYPES, pt);
                  if (!config) return null;
                  const { icon: Icon, label, tooltip } = config;
                  return (
                    <PlaybookBadge
                      key={pt}
                      label={label}
                      icon={Icon}
                      tooltip={tooltip}
                    />
                  );
                })}
                {integrationConfig && <PlaybookBadge {...integrationConfig} />}
              </>
            ) : (
              <>
                <SkeletonLine className="w-12" subtle />
                <SkeletonLine className="w-12" subtle />
                <SkeletonLine className="w-12" subtle />
              </>
            )}
          </div>
          {guide &&
            "update_notes" in guide &&
            guide.update_notes &&
            guide.published_at && (
              <div className="mt-xl rounded-lg border border-secondary bg-primary_alt p-xl text-sm text-secondary xl:max-w-[calc(100%-408px)]">
                <strong>{formatDate(guide.published_at)} Update:</strong>{" "}
                {guide.update_notes}
              </div>
            )}
        </div>

        <PlaybookContentLayout
          title={guide?.title}
          body={guide && "body" in guide ? guide.body : undefined}
          author={guide && "author" in guide ? guide.author : undefined}
          authorSkeleton
          headingsSkeleton
        >
          {guide && "body" in guide ? (
            <div>
              <PlaybookBody body={guide.body} />
            </div>
          ) : (
            isLoading && <GuideBodySkeleton />
          )}
          {guide && "faqs" in guide && guide.faqs.length > 0 && (
            <div className="mt-6xl flex flex-col gap-4xl">
              <h2 className="text-display-md font-semibold">FAQs</h2>
              <PlaybookGuideFAQs faqs={guide.faqs} />
            </div>
          )}
          {guide && "body" in guide && (
            <PlaybookDisclaimer className="mt-7xl" />
          )}
        </PlaybookContentLayout>
        {guide &&
          "recommended_guides" in guide &&
          guide.recommended_guides.length > 0 && (
            <>
              <hr className="border-secondary" />
              <PlaybookGuideCardRow
                title="Recommended Guides"
                guides={guide.recommended_guides}
              />
            </>
          )}
        {section && otherSectionGuides.length > 0 && (
          <>
            <hr className="border-secondary" />
            <PlaybookGuideCardRow
              title={section.title}
              guides={otherSectionGuides}
              viewAllHref={playbookSectionLink(section)}
            />
          </>
        )}
      </article>
    </div>
  );
};
